/**
 * Gaushala Access Wrapper Component
 *
 * Checks if user has gaushala access before rendering the main GauShala component
 * This wrapper properly handles the access check without breaking React hooks rules
 *
 * Note: Global data prefetch happens in GlobalPrefetchContext on app mount,
 * so we don't need module-level prefetch here (avoids duplicate network requests)
 */

import React, { useEffect, useState } from 'react';
import GauShala from '../GauShala';
import NoGaushalaAssigned from './NoGaushalaAssigned';
import { getGaushalaProfile, GaushalaError } from '@/services/gaushala/profileService';

type AccessState = 'checking' | 'allowed' | 'denied';

/**
 * GaushalaAccessWrapper Component
 * Renders the appropriate component based on gaushala access status
 */
export const GaushalaAccessWrapper: React.FC = () => {
  const [accessState, setAccessState] = useState<AccessState>('checking');

  // Check gaushala access on mount
  useEffect(() => {
    const checkAccess = async () => {
      try {
        await getGaushalaProfile();
        setAccessState('allowed');
      } catch (error) {
        if (error instanceof GaushalaError && error.code === 'NO_GAUSHALA_ASSIGNED') {
          setAccessState('denied');
        } else {
          // Allow access for other errors
          setAccessState('allowed');
        }
      }
    };

    checkAccess();
  }, []);

  // Show loading spinner
  if (accessState === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Gaushala...</p>
        </div>
      </div>
    );
  }

  // Show no gaushala page if denied
  if (accessState === 'denied') {
    return <NoGaushalaAssigned />;
  }

  // Show main gaushala dashboard if allowed
  return <GauShala />;
};

export default GaushalaAccessWrapper;
